import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getPolicy, deletePolicy } from "@/services/policiesApi";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Settings, Loader2, Tag, Calendar, User, Lock, Pencil } from "lucide-react";
import { inferProfileIconFromPolicy, getIconComponent } from "@/utils/profileIcons";
import { buildProfileActions } from "@/components/actions";
import { format } from "date-fns";
import { parseDateTime } from "@/utils/dateUtils";
import { TagsDisplay } from "@/components/tags/TagsDisplay";
import { TagAssignmentDialog } from "@/components/tags/TagAssignmentDialog";
import { toast } from "sonner";
import { PayloadDetailCard } from "@/components/configurations/PayloadDetailCard";
import { DetailPageLayout } from "@/components/ui/detail-page-layout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ProfileDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTagAssignmentOpen, setIsTagAssignmentOpen] = useState(false);

  const { data: policy, isLoading, error } = useQuery({
    queryKey: ['policy', id],
    queryFn: () => getPolicy(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !policy) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Profile Not Found</h2>
          <p className="text-muted-foreground mb-4">
            {error instanceof Error ? error.message : "The profile you're looking for doesn't exist."}
          </p>
          <Button onClick={() => navigate("/configurations")}>
            Back to Configurations
          </Button>
        </div>
      </div>
    );
  }

  const handleDelete = () => setShowDeleteDialog(true);

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      await deletePolicy(id!);
      // Drop cached list + this policy so the listing reflects the delete
      // immediately (avoids the 10-min staleTime making it look broken).
      await queryClient.invalidateQueries({ queryKey: ['policies'] });
      queryClient.removeQueries({ queryKey: ['policy', id] });
      toast.success("Configuration deleted");
      navigate("/configurations");
    } catch (err) {
      toast.error("Failed to delete configuration");
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const payloads = policy.payloads || [];

  const payloadIdentifiers = [
    (policy as any).payloadType,
    ...payloads.map((pl: any) => pl?.identifier || pl?.kind || pl?.payloadId || ""),
  ];
  const ProfileIcon = getIconComponent(
    inferProfileIconFromPolicy(policy.name, payloadIdentifiers)
  );

  const created = parseDateTime((policy as any).createdAt);
  const updated = parseDateTime((policy as any).updatedAt);
  const createdBy = (policy as any).createdBy || (policy as any).created_by;

  return (
    <DetailPageLayout
      backLabel="Back to Configurations"
      onBack={() => navigate("/configurations")}
      
      actions={buildProfileActions({
        surface: "detail",
        onView: () => {},
        onEdit: () => navigate(`/configurations/${id}/edit`),
        onDuplicate: () => toast.success("Profile duplicated successfully"),
        onAssignTags: () => setIsTagAssignmentOpen(true),
        onDelete: handleDelete,
      })}
      icon={<ProfileIcon className="h-8 w-8 text-primary" />}
      title={policy.name}
      description={policy.description}
      heroExtra={
        <div className="flex items-center space-x-3">
          <Badge variant="outline">{policy.payloadType || "Configuration"}</Badge>
        </div>
      }
      dialogs={
        <>
          <TagAssignmentDialog
            open={isTagAssignmentOpen}
            onOpenChange={setIsTagAssignmentOpen}
            resourceId={policy.id}
            resourceType="profile"
            resourceName={policy.name}
          />
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Profile</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{policy.name}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isDeleting ? "Deleting..." : "Delete Profile"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      }
    >
      <div
        role="status"
        aria-live="polite"
        className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border bg-muted/40 px-4 py-3"
      >
        <div className="flex items-start gap-2 text-sm">
          <Lock className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" aria-hidden />
          <div>
            <p className="font-medium">You're viewing this profile in read-only mode</p>
            <p className="text-muted-foreground text-xs">
              Settings shown below cannot be changed here. Click Edit to modify them.
            </p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => navigate(`/configurations/${id}/edit`)}
          className="shrink-0"
        >
          <Pencil className="h-4 w-4 mr-2" />
          Edit profile
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* General Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">General Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Name</label>
                <p className="font-medium">{policy.name}</p>
              </div>
              {policy.description && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Description</label>
                  <p className="text-sm">{policy.description}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-muted-foreground">Payload Type</label>
                <p className="text-sm">{policy.payloadType || "N/A"}</p>
              </div>
              {policy.linkedCertPolicyIds && policy.linkedCertPolicyIds.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Linked Certificate Policies</label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {policy.linkedCertPolicyIds.map((certId: string) => (
                      <Badge key={certId} variant="secondary" className="text-xs">{certId}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payloads */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Configuration Payloads
                <Badge variant="secondary" className="ml-auto">
                  {payloads.length} configured
                </Badge>
              </CardTitle>
              <CardDescription>
                Settings and restrictions applied by this profile
              </CardDescription>
            </CardHeader>
            <CardContent>
              {payloads.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Settings className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No payloads configured</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payloads.map((payload: any, index: number) => (
                    <PayloadDetailCard key={payload.policyPayloadId || index} payload={payload} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Policy Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Policy ID</label>
                <p className="text-xs font-mono text-muted-foreground break-all">{policy.id}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Payload Type</label>
                <p className="text-sm">{policy.payloadType || "N/A"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Total Payloads</label>
                <p className="text-sm">{payloads.length}</p>
              </div>
            </CardContent>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Tags
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => setIsTagAssignmentOpen(true)}>
                Assign Tags
              </Button>
            </CardHeader>
            <CardContent>
              <TagsDisplay resourceId={policy.id} resourceType="profile" maxTags={10} showRemoveButton />
            </CardContent>
          </Card>

          {/* Metadata */}
          {(created || updated || createdBy) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Metadata
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {created && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Created</label>
                    <p className="text-sm">{format(new Date(created), "MMM d, yyyy 'at' h:mm a")}</p>
                  </div>
                )}
                {updated && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Last Modified</label>
                    <p className="text-sm">{format(new Date(updated), "MMM d, yyyy 'at' h:mm a")}</p>
                  </div>
                )}
                {createdBy && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                      <User className="h-3 w-3" /> Created By
                    </label>
                    <p className="text-sm">{createdBy}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DetailPageLayout>
  );
}
